import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  CopyButton,
  Group,
  Modal,
  PasswordInput,
  Stack,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { IconAlertTriangle, IconCopy, IconKey } from '@tabler/icons-react';
import { DataTableColumn } from 'mantine-datatable';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { formatShortDate } from 'src/utils/dateFormatting';
import { useCreateTenantApiKey } from './hooks/useCreateTenantApiKey';
import { useRevokeTenantApiKey } from './hooks/useRevokeTenantApiKey';

type TenantApiKey = {
  id: string;
  name: string;
  createdAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export default function TenantApiKeysManager({
  companyId,
  title = 'API keys',
}: {
  companyId?: string;
  title?: string;
}) {
  const [createdRawKey, setCreatedRawKey] = useState<string | null>(null);
  const [keyToRevoke, setKeyToRevoke] = useState<TenantApiKey | null>(null);
  const [generateOpened, { open: openGenerate, close: closeGenerate }] = useDisclosure(false);
  const [revealOpened, { open: openReveal, close: closeReveal }] = useDisclosure(false);

  const { createKey, isLoading: isCreating } = useCreateTenantApiKey(companyId);
  const { revokeKey, isLoading: isRevoking } = useRevokeTenantApiKey(companyId);

  const form = useForm({
    initialValues: { name: '' },
    validate: { name: v => (v.trim() ? null : 'Name is required') },
  });

  const handleGenerate = async (values: { name: string }) => {
    try {
      const result = await createKey({ name: values.name });
      setCreatedRawKey(result.data.rawKey);
      form.reset();
      closeGenerate();
      openReveal();
    } catch (error) {
      notifyAndLogError(
        'Failed to generate API key',
        'An error occurred while generating the key.',
        error,
      );
    }
  };

  const handleCloseReveal = () => {
    setCreatedRawKey(null);
    closeReveal();
  };

  const handleRevoke = async () => {
    if (!keyToRevoke) return;
    try {
      await revokeKey(keyToRevoke.id);
      setKeyToRevoke(null);
    } catch (error) {
      notifyAndLogError(
        'Failed to revoke API key',
        'An error occurred while revoking the key.',
        error,
      );
    }
  };

  const columns: DataTableColumn<TenantApiKey>[] = [
    { accessor: 'name', title: 'Name' },
    {
      accessor: 'createdAt',
      title: 'Created',
      render: row => (row.createdAt ? formatShortDate(row.createdAt) : '—'),
    },
    {
      accessor: 'lastUsedAt',
      title: 'Last used',
      render: row => (row.lastUsedAt ? formatShortDate(row.lastUsedAt) : '—'),
    },
    {
      accessor: 'revokedAt',
      title: 'Status',
      render: row =>
        row.revokedAt ? <Badge color="red">Revoked</Badge> : <Badge color="green">Active</Badge>,
    },
    {
      accessor: 'actions',
      title: 'Actions',
      textAlign: 'right',
      render: row =>
        row.revokedAt ? null : (
          <Button
            size="xs"
            color="red"
            variant="light"
            onClick={e => {
              e.stopPropagation();
              setKeyToRevoke(row);
            }}
          >
            Revoke
          </Button>
        ),
    },
  ];

  return (
    <>
      <Modal opened={generateOpened} onClose={closeGenerate} title="Generate API key">
        <form onSubmit={form.onSubmit(handleGenerate)}>
          <Stack>
            <TextInput
              label="Key name"
              placeholder="e.g. Origo integration"
              key={form.key('name')}
              {...form.getInputProps('name')}
            />
            <Group justify="flex-end">
              <Button variant="light" color="gray" onClick={closeGenerate}>
                Cancel
              </Button>
              <Button type="submit" loading={isCreating}>
                Generate
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={revealOpened}
        onClose={handleCloseReveal}
        title="API key generated"
        closeOnClickOutside={false}
        closeOnEscape={false}
        size="lg"
      >
        <Stack>
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            Copy this key now — for security it will not be shown again.
          </Alert>
          <PasswordInput
            label="Your new API key"
            value={createdRawKey ?? ''}
            readOnly
            styles={{ innerInput: { fontFamily: 'monospace' } }}
          />
          <Group justify="flex-end">
            <CopyButton value={createdRawKey ?? ''}>
              {({ copied, copy }) => (
                <Button variant="light" leftSection={<IconCopy size={16} />} onClick={copy}>
                  {copied ? 'Copied!' : 'Copy key'}
                </Button>
              )}
            </CopyButton>
            <Button onClick={handleCloseReveal}>Done</Button>
          </Group>
        </Stack>
      </Modal>

      <ConfirmDeleteModal
        opened={keyToRevoke !== null}
        onClose={() => setKeyToRevoke(null)}
        onConfirm={handleRevoke}
        title="Revoke API key"
        message={`Are you sure you want to revoke "${keyToRevoke?.name}"? Any application using this key will immediately lose access. This action cannot be undone.`}
        confirmLabel="Revoke"
        isLoading={isRevoking}
      />

      <SimpleTable<TenantApiKey>
        resource={companyId ? `admin/companies/${companyId}/api-keys` : 'dev-tools/api-keys'}
        columns={columns}
        title={title}
        titleIcon={<IconKey size={18} />}
        showCount
        enableSearch={false}
        pageSize={20}
        primaryButton={{ label: 'Generate key', onClick: openGenerate }}
      />
    </>
  );
}
